import autoTable from 'jspdf-autotable';

import { generateColumnDefs } from 'components/practice/BeyondBilling/Reports/components/ReportView/lib/generateColumnDefs';

import { generateFooter } from '../pdfFooter';
import { generateHeader } from '../pdfHeader';

import { GAP, PAGE_CHILD_COLOR, PAGE_COLOR, TABLE_FONT_SIZE } from './constants';
import extractDataFromColDefs from './extractDataFromColDefs';
import {
  didDrawCell,
  formatCells,
  generatePageNumberAndDate,
  getColumnStyles,
  splitRowsChunk,
  splitSmartChunks
} from './helpers';

export const generateTable = ({
  doc,
  category,
  startY,
  columnHeaders,
  rows,
  orientation,
  maxColumns,
  totalsSum,
  configs = {}
}) => {
  const edgeMargin = columnHeaders.length > 5 ? 0 : GAP;

  const columnChunks = splitSmartChunks(columnHeaders, maxColumns);
  const rowsChunk =
    columnChunks.length === 1 // don't split rows when columnChunk is 1
      ? [rows]
      : splitRowsChunk({ doc, startY, rows, orientation, columnChunks });

  rowsChunk.forEach((chunkRows, chunkRowIndex) => {
    let rowsSlice = 0;

    // Add new page for each new row chunk (after the first)
    if (chunkRowIndex > 0) doc.addPage();

    let prevChunkLength = 0;

    columnChunks.forEach((headerChunk, columnChunkIndex) => {
      const tableRows = chunkRows.map((row) =>
        row.slice(rowsSlice, rowsSlice + headerChunk.length)
      );

      if (columnChunkIndex > 0) doc.addPage();

      let foot;
      if (totalsSum?.length && chunkRowIndex === rowsChunk.length - 1) {
        foot = [
          totalsSum.slice(
            columnChunkIndex * prevChunkLength,
            columnChunkIndex * prevChunkLength + headerChunk.length
          )
        ];
      }

      // update prevChunkLength for next chunk
      prevChunkLength = headerChunk.length;

      autoTable(doc, {
        head: [headerChunk],
        body: tableRows,
        styles: { fontSize: TABLE_FONT_SIZE },
        foot,
        startY: chunkRowIndex === 0 && columnChunkIndex === 0 ? startY : 10,
        showFoot: totalsSum ? 'lastPage' : false,
        didParseCell: (data) => formatCells(data, foot),
        didDrawCell,
        ...configs,
        margin: { left: edgeMargin, right: edgeMargin, ...(configs?.margin || {}) },
        headStyles: {
          fillColor: PAGE_COLOR,
          overflow: 'linebreak',
          ...(configs?.headStyles || {})
        },
        footStyles: { fontStyle: 'bold', align: 'right', ...(configs?.footStyles || {}) },
        columnStyles: {
          ...getColumnStyles(headerChunk, rows, category, rowsSlice),
          ...(configs?.columnStyles || {})
        }
      });

      rowsSlice += headerChunk.length;
    });
  });

  generatePageNumberAndDate(doc);

  const finalY = doc.lastAutoTable.finalY;
  return finalY;
};

//generate the complete table
export const generateCompleteTable = ({
  doc,
  category,
  columnHeaders,
  rows,
  orientation,
  maxColumns,
  totalsSum,
  headerConfigs = {},
  footerConfigs = {}
}) => {
  const startY = generateHeader({ doc, headerConfigs });

  const endY = generateTable({
    doc,
    category,
    startY: startY + GAP / 2,
    columnHeaders,
    rows,
    orientation,
    maxColumns,
    totalsSum
  });

  return generateFooter({ doc, endY, headerConfigs, footerConfigs });
};

// recursively add table for each row if it has children
export const addNestedTable = ({ doc, data = [], startY = 0, columns, isNested = false }) => {
  if (!data.length) return;

  let updatedStartY = startY;

  const finalColumns = columns || generateColumnDefs(data);
  const formattedData = extractDataFromColDefs(data, finalColumns);
  const rows = formattedData.map((row) => finalColumns.map((col) => row[col.field]));

  const handleGenerateTable = (y = startY, rows2 = rows) => {
    return generateTable({
      doc,
      columnHeaders: finalColumns.map((col) => col.headerName),
      rows: rows2,
      startY: y,
      configs: {
        margin: { left: 0, right: 0 },
        ...(isNested && { headStyles: { fillColor: PAGE_CHILD_COLOR } })
      }
    });
  };

  if (!data?.[0]?.children) {
    return handleGenerateTable();
  }

  data.forEach((record, index) => {
    updatedStartY = handleGenerateTable(updatedStartY, [rows[index]]);

    if (record?.children?.length) {
      updatedStartY = addNestedTable({
        doc,
        data: record?.children,
        startY: updatedStartY,
        isNested: true
      });
    }
  });

  return updatedStartY;
};
